import { useState, useLayoutEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Rss, Radio } from "lucide-react";
import { siteConfig } from "@/config/siteConfig";
import { show, getLatest } from "@/lib/podcast";
import { usePlayer } from "@/context/PlayerContext";
import PlayerBar from "@/components/PlayerBar";

export const Layout = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { playEpisode, current } = usePlayer();
  const latest = getLatest();

  // Reset scroll to top on every route change, before paint, and again on the
  // next frame to override any child mount-time scroll.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    const raf = requestAnimationFrame(() => window.scrollTo(0, 0));
    return () => cancelAnimationFrame(raf);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-[#fbf8f1] text-[#374566] font-sans flex flex-col">
      <header
        data-testid="main-header-nav"
        className="sticky top-0 z-40 backdrop-blur-md bg-[#0b1f4d]/95 border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between gap-4">
            <Link
              to="/"
              data-testid="brand-logo-link"
              className="flex items-center gap-3 group shrink-0"
            >
              <img
                src={show.image}
                alt={show.title}
                className="h-9 w-9 rounded-md object-cover ring-1 ring-white/15"
              />
              <span className="font-display uppercase text-xl font-semibold tracking-wide text-white group-hover:text-[#f5b61c] transition-colors">
                {show.title}
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {siteConfig.nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`nav-link-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium uppercase tracking-wide font-display transition-colors ${
                      isActive
                        ? "text-[#f5b61c]"
                        : "text-neutral-400 hover:text-white"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-2">
              {latest && (
                <button
                  data-testid="header-latest-play-btn"
                  onClick={() => playEpisode(latest)}
                  className="inline-flex items-center gap-2 rounded-sm bg-[#f5b61c] hover:bg-[#dda00d] text-[#0a1630] text-sm font-semibold uppercase tracking-wide px-4 py-2 transition-colors focus-visible:ring-2 focus-visible:ring-[#f5b61c] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b1f4d] focus-visible:outline-none"
                >
                  <Radio size={16} /> Latest
                </button>
              )}
            </div>

            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 -mr-2 text-neutral-200"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {open && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden border-t border-white/10 bg-[#0b1f4d]"
          >
            <nav className="px-4 py-3 flex flex-col gap-1">
              {siteConfig.nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-3 py-3 text-base font-medium uppercase tracking-wide font-display rounded-sm ${
                      isActive
                        ? "text-[#f5b61c] bg-white/5"
                        : "text-neutral-300"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main className={`flex-1 ${current ? "pb-28" : ""}`} key={pathname}>
        <Outlet />
      </main>

      <footer
        data-testid="site-footer"
        className={`border-t border-white/10 bg-[#0b1f4d] text-neutral-300 ${
          current ? "mb-24" : ""
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sm:col-span-2">
            <div className="flex items-center gap-3">
              <img
                src={show.image}
                alt={show.title}
                className="h-10 w-10 rounded-md object-cover ring-1 ring-white/10"
              />
              <span className="font-display uppercase text-lg font-semibold tracking-wide text-white">
                {show.title}
              </span>
            </div>
            <p className="mt-4 text-sm leading-relaxed max-w-md">
              {show.description}
            </p>
          </div>
          <div>
            <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-[#f5b61c] mb-3">
              Explore
            </h4>
            <ul className="space-y-2 text-sm">
              {siteConfig.nav.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-[#f5b61c] mb-3">
              Subscribe
            </h4>
            <a
              href={siteConfig.feedUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-rss-link"
              className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors"
            >
              <Rss size={15} /> RSS Feed
            </a>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-neutral-500 flex flex-col sm:flex-row gap-2 justify-between">
            <span data-testid="footer-copyright">
              {(() => {
                const text = show.copyright || `© ${show.title}`;
                const name = "Johns Creek Studios, LLC";
                const idx = text.indexOf(name);
                if (idx === -1) return text;
                return (
                  <>
                    {text.slice(0, idx)}
                    <a
                      href="https://johnscreekstudios.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="footer-studio-link"
                      className="text-neutral-400 hover:text-[#f5b61c] underline underline-offset-2 transition-colors"
                    >
                      {name}
                    </a>
                    {text.slice(idx + name.length)}
                  </>
                );
              })()}
            </span>
            <span>{siteConfig.tagline} · Auto-updated from RSS</span>
          </div>
        </div>
      </footer>

      <PlayerBar />
    </div>
  );
};

export default Layout;
